/**
 * ReportsHome — flat list of every available report.
 *
 * Sources from the `reports` collection: system templates (org=null) +
 * the active org's private reports. Click a row to open it in the builder.
 *
 * Replaces the earlier per-category card grid, which only surfaced 4
 * pre-canned cohorts and hid the actual library of saved reports.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Wrench, ChevronRight, MailCheck } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/contexts/AuthContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { Card, Button, Badge, Spinner, EmptyState } from '@/components/ui';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/Alert';
import { Input } from '@/components/ui/Input';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import { listSavedReports } from '@/services/reportBuilderService';
import type { SavedReport, SavedReportCategory } from '@simcheck/shared-types';

const CATEGORY_LABELS: Record<SavedReportCategory, string> = {
  overview: 'Overview',
  data: 'Data',
  coverage: 'Coverage',
  hardware: 'Hardware',
  failures: 'Failures',
  voice: 'Voice',
  custom: 'Custom',
};

function formatRelative(date: Date | string | undefined | null, timeZone?: string): string {
  if (!date) return '—';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return d.toLocaleDateString(undefined, { timeZone });
}

export default function ReportsHome() {
  const { activeOrganization } = useAuth();
  const { timeZone } = useDateFormat();
  const navigate = useNavigate();
  const [reports, setReports] = useState<SavedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!activeOrganization?.id) return;
      setLoading(true);
      setError(null);
      try {
        const list = await listSavedReports(activeOrganization.id);
        if (!cancelled) setReports(list);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load reports');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [activeOrganization?.id]);

  const filtered = useMemo(() => {
    if (!search.trim()) return reports;
    const q = search.trim().toLowerCase();
    return reports.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q) ||
        CATEGORY_LABELS[r.category].toLowerCase().includes(q),
    );
  }, [reports, search]);

  const systemCount = reports.filter((r) => r.organizationId === null).length;
  const myCount = reports.length - systemCount;

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2">Reports</h1>
            <p className="text-body text-muted-foreground">
              {reports.length} report{reports.length === 1 ? '' : 's'} available
              {reports.length > 0 ? ` — ${systemCount} system, ${myCount} private` : ''}.
              Click any to open it in the builder.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Search reports..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-56"
            />
            <Button
              variant="outline"
              size="sm"
              icon={<MailCheck className="w-4 h-4" />}
              onClick={() => navigate('/reports/subscriptions')}
            >
              Deliveries
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={<Plus className="w-4 h-4" />}
              onClick={() => navigate('/reports/builder')}
            >
              New report
            </Button>
          </div>
        </div>

        {error ? (
          <Alert variant="destructive">
            <AlertTitle>Failed to load</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <Card padding="none">
          {loading && reports.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Spinner />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={<Wrench className="h-10 w-10" />}
                title={search ? 'No reports match your search' : 'No reports yet'}
                description={
                  search
                    ? 'Clear the search or pick a different term.'
                    : 'Open the builder to create your first one — pick rows, measures, filters, and save.'
                }
              />
            </div>
          ) : (
            <ResponsiveTable<SavedReport>
              data={filtered}
              keyExtractor={(r) => r.id}
              onRowClick={(r) => navigate(`/reports/builder?preset=${r.id}`)}
              paginated
              defaultPageSize={25}
              columns={[
                {
                  key: 'name',
                  header: 'Name',
                  sortable: true,
                  sortValue: (r: SavedReport) => r.name,
                  render: (_: unknown, r: SavedReport) => (
                    <div className="min-w-0">
                      <div className="font-medium truncate">{r.name}</div>
                      {r.description ? (
                        <div className="text-xs text-muted-foreground truncate">{r.description}</div>
                      ) : null}
                    </div>
                  ),
                },
                {
                  key: 'category',
                  header: 'Category',
                  sortable: true,
                  sortValue: (r: SavedReport) => CATEGORY_LABELS[r.category],
                  render: (_: unknown, r: SavedReport) => (
                    <Badge variant="outline" className="text-xs">{CATEGORY_LABELS[r.category]}</Badge>
                  ),
                },
                {
                  key: 'scope',
                  header: 'Scope',
                  sortable: true,
                  sortValue: (r: SavedReport) => (r.organizationId === null ? 'System' : 'Private'),
                  render: (_: unknown, r: SavedReport) => (
                    r.organizationId === null
                      ? <Badge variant="secondary" className="text-xs">System</Badge>
                      : <Badge variant="outline" className="text-xs">Private</Badge>
                  ),
                },
                {
                  key: 'updated',
                  header: 'Last updated',
                  sortable: true,
                  sortValue: (r: SavedReport) => new Date(r.updatedAt).getTime(),
                  render: (_: unknown, r: SavedReport) => (
                    <span className="text-sm text-muted-foreground tabular-nums">{formatRelative(r.updatedAt, timeZone)}</span>
                  ),
                },
                {
                  key: '__drill',
                  header: '',
                  render: () => <ChevronRight className="h-4 w-4 text-muted-foreground" />,
                },
              ]}
            />
          )}
        </Card>
      </div>
    </AppShell>
  );
}
